import React, { useState } from "react";

const competitors = [
  {
    id: "minutes",
    name: "Minutes",
    title: "Minutes: AI Meeting Note Taker",
    subtitle: "Transcribe, Record Voice Audio",
    ratings: "14K",
    stars: 4.9,
    screenshots: 6,
    color: "#6366f1",
    features: [
      "Word-for-word transcripts with speaker labels",
      "Specify keywords for AI to listen for",
      "Transcribe in one language, summarize in another",
      "One-click recording",
      "Ask questions & chat with recording",
    ],
    url: "https://apps.apple.com/us/app/minutes-ai-meeting-note-taker/id6504206467",
  },
  {
    id: "summary",
    name: "Summary",
    title: "Summary: AI Meeting Note Taker",
    subtitle: "Transcribe audio to text",
    ratings: "32K",
    stars: 4.7,
    screenshots: 7,
    color: "#8b5cf6",
    features: [
      "One-tap recording, unlimited time",
      "Speaker labels",
      "Ask questions about meetings",
      "Professional meeting notes sharing",
      "Apple Watch support",
    ],
    url: "https://apps.apple.com/us/app/summary-ai-meeting-note-taker/id6670175056",
  },
  {
    id: "hynote",
    name: "HyNote",
    title: "HyNote: AI Note Taker, Summary",
    subtitle: "AI Notebook, AI Meeting Notes",
    ratings: "6.4K",
    stars: 4.8,
    screenshots: 10,
    color: "#ec4899",
    features: [
      "Multi-type inputs: audio, images, PDFs, YouTube, web",
      "AI-powered summaries",
      "Live transcription",
      "Flashcards & quizzes (spaced repetition)",
      "Chat with content",
      "AI categorization & organization",
    ],
    closest: true,
    url: "https://apps.apple.com/us/app/hynote-ai-note-taker-summary/id6478655348",
  },
  {
    id: "granola",
    name: "Granola",
    title: "Granola - AI Meeting Notes",
    subtitle: "AI notes with Google Calendar",
    ratings: "4.8K",
    stars: 5.0,
    screenshots: 8,
    color: "#f59e0b",
    features: [
      "One-tap from Lock Screen",
      "Phone call recording",
      "Chat with notes",
      "Polished summaries in seconds",
      "iPhone + Mac sync",
      "Widgets for easy access",
    ],
    url: "https://apps.apple.com/us/app/granola-ai-meeting-notes/id6739429409",
  },
  {
    id: "flownote",
    name: "Flownote",
    title: "Flownote AI Meeting Note Taker",
    subtitle: "Record, Transcribe, Summarize",
    ratings: "2.3K",
    stars: 4.8,
    screenshots: 8,
    color: "#10b981",
    features: [
      "Speaker identification",
      "Instant AI summaries with action items",
      "Chat with your notes",
      "Folder organization",
      "Works for remote + in-person",
    ],
    url: "https://apps.apple.com/us/app/flownote-ai-meeting-note-taker/id6501961836",
  },
  {
    id: "cue",
    name: "Cue",
    title: "Cue - Smart AI Meeting Notes",
    subtitle: "Live audio to text companion",
    ratings: "2.5K",
    stars: 4.4,
    screenshots: 8,
    color: "#06b6d4",
    features: [
      "Hands-free, distraction-free",
      "Privacy & security first",
      "Multiple speakers + technical jargon",
      "Multilingual transcribe + summarize",
      "Search & ask questions",
    ],
    url: "https://apps.apple.com/us/app/cue-smart-ai-meeting-notes/id6741517781",
  },
  {
    id: "manus",
    name: "Manus",
    title: "Manus AI",
    subtitle: "AI Agent",
    ratings: "22K",
    stars: 4.8,
    screenshots: 0,
    color: "#34322d",
    features: [
      "Task execution with to-do breakdowns",
      "Slide deck generation",
      "File-to-website conversion",
      "AI-powered design and automation",
    ],
    url: "https://apps.apple.com/us/app/manus-ai/id6740909540",
  },
];

const youwoAdvantages = [
  { feature: "Per-word replay highlighting", hasIt: "Only YouWo" },
  { feature: "Live translation alongside transcript", hasIt: "Only YouWo" },
  { feature: "AI summaries with timestamp citations", hasIt: "Only YouWo" },
  { feature: "60+ language support", hasIt: "Broadest" },
  { feature: "Multi-format: meetings + PDFs + YouTube + images + links", hasIt: "YouWo + HyNote" },
  { feature: "Flashcards & quizzes from any content", hasIt: "YouWo + HyNote" },
  { feature: "User annotations on transcript parts", hasIt: "Only YouWo" },
];

export default function CompetitorAnalysis() {
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "40px 24px", fontFamily: "Inter, -apple-system, sans-serif" }}>
      <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>Competitor Analysis</h1>
      <p style={{ color: "#6b7280", marginBottom: 32 }}>
        App Store screenshots and positioning from AI meeting notes competitors. Click a card to see screenshots.
      </p>

      {/* YouWo Advantages */}
      <div style={{ background: "#f0f9ff", borderRadius: 12, padding: 20, marginBottom: 32, border: "1px solid #bae6fd" }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: "#0369a1" }}>YouWo Unique Advantages</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 8 }}>
          {youwoAdvantages.map((a) => (
            <div key={a.feature} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
              <span style={{ color: a.hasIt === "Only YouWo" ? "#059669" : "#d97706", fontWeight: 600, minWidth: 100, fontSize: 11 }}>
                {a.hasIt}
              </span>
              <span style={{ color: "#374151" }}>{a.feature}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Competitor Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 16, marginBottom: 32 }}>
        {competitors.map((c) => (
          <div
            key={c.id}
            onClick={() => setSelected(selected === c.id ? null : c.id)}
            style={{
              border: selected === c.id ? `2px solid ${c.color}` : "1px solid #e5e7eb",
              borderRadius: 12,
              padding: 16,
              cursor: "pointer",
              background: selected === c.id ? "#fafafa" : "#fff",
              transition: "all 0.15s",
              position: "relative",
            }}
          >
            {c.closest && (
              <span style={{
                position: "absolute", top: -8, right: 12, background: "#ef4444", color: "#fff",
                fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4,
              }}>
                CLOSEST COMPETITOR
              </span>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{c.name}</h3>
                <p style={{ fontSize: 12, color: "#6b7280", margin: "2px 0 0" }}>{c.subtitle}</p>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{c.stars}</div>
                <div style={{ fontSize: 11, color: "#9ca3af" }}>{c.ratings} ratings</div>
              </div>
            </div>
            <p style={{ fontSize: 12, color: "#9ca3af", margin: "0 0 8px", fontStyle: "italic" }}>{c.title}</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {c.features.slice(0, 3).map((f) => (
                <span key={f} style={{ fontSize: 11, background: "#f3f4f6", color: "#374151", padding: "2px 8px", borderRadius: 4 }}>
                  {f}
                </span>
              ))}
              {c.features.length > 3 && (
                <span style={{ fontSize: 11, color: "#9ca3af" }}>+{c.features.length - 3} more</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Screenshots for selected competitor */}
      {selected && (
        <div style={{ marginBottom: 40 }}>
          <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 16 }}>
            {competitors.find((c) => c.id === selected)?.name} — App Store Screenshots
          </h2>
          <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 16 }}>
            {Array.from({ length: competitors.find((c) => c.id === selected)?.screenshots || 0 }).map((_, i) => (
              <img
                key={i}
                src={`/internal/competitor-screenshots/${selected}/${i + 1}.webp`}
                alt={`${selected} screenshot ${i + 1}`}
                style={{
                  height: 500,
                  borderRadius: 12,
                  flexShrink: 0,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                }}
              />
            ))}
          </div>
          <a
            href={competitors.find((c) => c.id === selected)?.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 13, color: "#3b82f6" }}
          >
            View on App Store →
          </a>
        </div>
      )}

      {/* All screenshots side by side for quick comparison */}
      <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 16 }}>First Screenshot Comparison</h2>
      <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
        The first screenshot is what users see in search results — the most important one.
      </p>
      <div style={{ display: "flex", gap: 16, overflowX: "auto", paddingBottom: 16 }}>
        {competitors.map((c) => (
          <div key={c.id} style={{ flexShrink: 0, textAlign: "center" }}>
            <img
              src={`/internal/competitor-screenshots/${c.id}/1.webp`}
              alt={`${c.name} first screenshot`}
              style={{ height: 400, borderRadius: 10, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
            />
            <p style={{ fontSize: 12, fontWeight: 600, marginTop: 8 }}>{c.name}</p>
            <p style={{ fontSize: 11, color: "#6b7280" }}>{c.subtitle}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
